import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { Card, CardContent, CardHeader } from '@/components/ui/Card'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'

interface RetentionData {
  cohortMonth: string
  cohortSize: number
  retention: {
    month: string
    monthOffset: number
    activeUsers: number
    retentionRate: number
  }[]
}

function getDefaultCohort(): string {
  const d = new Date()
  d.setMonth(d.getMonth() - 1)
  return d.toISOString().slice(0, 7)
}

export function RetentionPage() {
  const [cohortMonth, setCohortMonth] = useState(getDefaultCohort)

  const { data, isLoading } = useQuery({
    queryKey: ['admin-analytics-retention', cohortMonth],
    queryFn: () =>
      api.get<RetentionData>('/api/admin/analytics/retention', {
        params: { cohort_month: cohortMonth },
      }),
    enabled: !!cohortMonth,
  })

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-heading font-semibold">Retention</h1>
        <input
          type="month"
          value={cohortMonth}
          onChange={(e) => setCohortMonth(e.target.value)}
          className="px-3 py-1.5 text-sm rounded-lg bg-[var(--surface-elevated)] border border-[var(--border)] text-[var(--content)] cursor-pointer"
        />
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12"><LoadingSpinner /></div>
      ) : data?.cohortSize === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-[var(--content-tertiary)]">
            No users in this cohort
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Cohort info */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <KpiCard label="Cohort Month" value={data?.cohortMonth ?? '--'} />
            <KpiCard label="Cohort Size" value={data?.cohortSize?.toLocaleString() ?? '0'} />
          </div>

          {/* Retention curve */}
          <Card className="mb-6">
            <CardHeader>
              <h3 className="font-heading font-semibold text-sm">Retention Curve</h3>
            </CardHeader>
            <CardContent>
              <div className="flex items-end gap-1 h-40">
                {(data?.retention ?? []).map((r) => (
                  <div key={r.month} className="flex-1 flex flex-col items-center">
                    <div
                      className="w-full bg-[#65A0A8]/60 hover:bg-[#65A0A8] rounded-t transition-colors"
                      style={{ height: `${Math.max(2, r.retentionRate)}%` }}
                      title={`${r.month}: ${r.activeUsers} users (${r.retentionRate.toFixed(0)}%)`}
                    />
                    <span className="text-[9px] text-[var(--content-tertiary)] mt-1 tabular-nums">
                      M{r.monthOffset}
                    </span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-[var(--content-tertiary)] text-center mt-2">Months since joining</p>
            </CardContent>
          </Card>

          {/* Retention table */}
          <Card>
            <CardHeader>
              <h3 className="font-heading font-semibold text-sm">Monthly Breakdown</h3>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[var(--border)]">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[var(--content-secondary)] uppercase tracking-wider">Month</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-[var(--content-secondary)] uppercase tracking-wider">Offset</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-[var(--content-secondary)] uppercase tracking-wider">Active</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-[var(--content-secondary)] uppercase tracking-wider">Retention</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.retention ?? []).map((r) => (
                    <tr key={r.month} className="border-b border-[var(--border-subtle)]">
                      <td className="px-4 py-2.5 tabular-nums">{r.month}</td>
                      <td className="px-4 py-2.5 text-right text-[var(--content-secondary)]">+{r.monthOffset}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums font-medium">{r.activeUsers}</td>
                      <td className="px-4 py-2.5 text-right">
                        <span className={`tabular-nums font-medium ${
                          r.retentionRate >= 50 ? 'text-emerald-600 dark:text-emerald-400' :
                          r.retentionRate >= 20 ? 'text-amber-600 dark:text-amber-400' :
                          'text-red-600 dark:text-red-400'
                        }`}>
                          {r.retentionRate.toFixed(1)}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}

function KpiCard({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardContent className="py-3">
        <p className="text-xs text-[var(--content-tertiary)] uppercase tracking-wider mb-1">{label}</p>
        <p className="text-xl font-heading font-semibold tabular-nums">{value}</p>
      </CardContent>
    </Card>
  )
}
